import { Checkbox, Dialog, DialogType, Dropdown, getTheme, IDropdownOption, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import React, { useEffect, useMemo, useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';

function getCurrentTimeString(): string {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** Parse "HH:mm" to minutes since midnight; clamp to valid range. */
function parseTimeToMinutes(s: string): number {
    const [h = 0, m = 0] = s.split(':').map((x) => parseInt(x, 10) || 0);
    return (Math.max(0, Math.min(23, h)) * 60 + Math.max(0, Math.min(59, m))) % (24 * 60);
}

/** Duration in minutes from start to end; if end < start, treat end as next day. */
function minutesBetween(startStr: string, endStr: string): number {
    const start = parseTimeToMinutes(startStr);
    let end = parseTimeToMinutes(endStr);
    if (end <= start) end += 24 * 60;
    return end - start;
}

/** Normalize "HH:mm" input to valid string. */
function normalizeTimeString(s: string): string {
    const [h = 0, m = 0] = s.split(':').map((x) => parseInt(x, 10) || 0);
    const hh = Math.max(0, Math.min(23, h));
    const mm = Math.max(0, Math.min(59, m));
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export interface IFocusTimeModalProps {
    isOpen: boolean;
    onDone: (endTimeTimestamp: number, durationMinutes: number, knockDoor: boolean, textMessage: boolean) => void;
    onDismiss: () => void;
}

const MAX_DURATION_MINUTES = 180; // 3 hours max

const DURATION_PRESETS = [15, 30, 45, 60, 120] as const; // minutes
const EXTENDED_DURATIONS = [15, 30, 45, 60, 90, 120, 150, 180] as const; // minutes, includes 1.5h, 2.5h, 3h (no 4h)

function getDurationLabel(minutes: number): string {
    switch (minutes) {
        case 15:
            return IvicosStrings.focusTime15Min;
        case 30:
            return IvicosStrings.focusTime30Min;
        case 45:
            return IvicosStrings.focusTime45Min;
        case 60:
            return IvicosStrings.focusTime1H;
        case 90:
            return IvicosStrings.focusTime1_5H;
        case 120:
            return IvicosStrings.focusTime2H;
        case 150:
            return IvicosStrings.focusTime2_5H;
        case 180:
            return IvicosStrings.focusTime3H;
        default:
            return `${minutes} min`;
    }
}

/**
 * Modal for specifying focus time (Do Not Disturb).
 * User configures duration, time interval, and emergency contact preferences.
 * Status is only set when user clicks Done.
 */
const EXTENDED_PRESET_VALUES = new Set<number>(EXTENDED_DURATIONS);

const FocusTimeModal: React.FC<IFocusTimeModalProps> = ({ isOpen, onDone, onDismiss }) => {
    const theme = getTheme();
    const [selectedPreset, setSelectedPreset] = useState<number | null>(15);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('09:15');
    const [knockDoor, setKnockDoor] = useState(true);
    const [textMessage, setTextMessage] = useState(true);
    const [maxDurationError, setMaxDurationError] = useState(false);
    const [timePastError, setTimePastError] = useState(false);

    const durationDropdownOptions: IDropdownOption[] = useMemo(() => EXTENDED_DURATIONS.map((min) => ({ key: min, text: getDurationLabel(min) })), []);

    const rawMinutes = useMemo(() => minutesBetween(startTime, endTime), [startTime, endTime]);
    const totalMinutes = Math.max(0, Math.min(MAX_DURATION_MINUTES, rawMinutes));

    const setEndFromStartAndDuration = (start: string, durationMin: number) => {
        const clamped = Math.max(0, Math.min(MAX_DURATION_MINUTES, durationMin));
        const startM = parseTimeToMinutes(start);
        const endM = startM + clamped;
        const endH = Math.floor(endM / 60) % 24;
        const endMin = endM % 60;
        setEndTime(`${String(endH).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`);
    };

    const isStartTimeInPast = (start: string): boolean => {
        const now = new Date();
        const [h, m] = start.split(':').map(Number);
        const startM = h * 60 + m;
        const nowM = now.getHours() * 60 + now.getMinutes();
        return startM < nowM;
    };

    const isEndTimeInPast = (start: string, end: string): boolean => {
        const now = new Date();
        const [eh, em] = end.split(':').map(Number);
        const endM = eh * 60 + em;
        const nowM = now.getHours() * 60 + now.getMinutes();
        const startM = parseTimeToMinutes(start);
        // End is in past: end < now, and end is same day as start (i.e. end >= start)
        if (endM >= startM) return endM < nowM; // same-day interval
        return false; // end next day, never "in past"
    };

    useEffect(() => {
        if (isOpen) {
            const now = getCurrentTimeString();
            setStartTime(now);
            setSelectedPreset(15);
            setEndFromStartAndDuration(now, 15);
            setMaxDurationError(false);
        }
    }, [isOpen]);

    // Auto-clamp to 3h when duration exceeds max; show error
    useEffect(() => {
        if (rawMinutes > MAX_DURATION_MINUTES) {
            setMaxDurationError(true);
            setEndFromStartAndDuration(startTime, MAX_DURATION_MINUTES);
            setSelectedPreset(EXTENDED_PRESET_VALUES.has(MAX_DURATION_MINUTES) ? MAX_DURATION_MINUTES : null);
        } else {
            setMaxDurationError(false);
        }
    }, [rawMinutes, startTime]);

    useEffect(() => {
        setTimePastError(isStartTimeInPast(startTime) || isEndTimeInPast(startTime, endTime));
    }, [startTime, endTime]);

    const handleDurationPreset = (minutes: number) => {
        const clamped = Math.min(MAX_DURATION_MINUTES, minutes);
        setSelectedPreset(clamped);
        setEndFromStartAndDuration(startTime, clamped);
        setMaxDurationError(minutes > MAX_DURATION_MINUTES);
    };

    const handleStartTimeChange = (_: unknown, value?: string) => {
        const v = normalizeTimeString(value || startTime);
        setStartTime(v);
        setSelectedPreset(null);
    };

    const handleEndTimeChange = (_: unknown, value?: string) => {
        const v = normalizeTimeString(value || endTime);
        setEndTime(v);
        setSelectedPreset(null);
        const newRaw = minutesBetween(startTime, v);
        setMaxDurationError(newRaw > MAX_DURATION_MINUTES);
    };

    const handleCustomHoursChange = (_: unknown, value?: string) => {
        const n = Math.max(0, Math.min(23, parseInt(value || '0', 10) || 0));
        const customMin = totalMinutes % 60;
        let durationMin = n * 60 + customMin;
        const exceeded = durationMin > MAX_DURATION_MINUTES;
        if (exceeded) durationMin = MAX_DURATION_MINUTES;
        setEndFromStartAndDuration(startTime, durationMin);
        setSelectedPreset(EXTENDED_PRESET_VALUES.has(durationMin) ? durationMin : null);
        setMaxDurationError(exceeded);
    };

    const handleCustomMinutesChange = (_: unknown, value?: string) => {
        const n = Math.max(0, Math.min(59, parseInt(value || '0', 10) || 0));
        let durationMin = Math.floor(totalMinutes / 60) * 60 + n;
        const exceeded = durationMin > MAX_DURATION_MINUTES;
        if (exceeded) durationMin = MAX_DURATION_MINUTES;
        setEndFromStartAndDuration(startTime, durationMin);
        setSelectedPreset(EXTENDED_PRESET_VALUES.has(durationMin) ? durationMin : null);
        setMaxDurationError(exceeded);
    };

    const handleDone = () => {
        if (timePastError || totalMinutes <= 0) return;
        const [h, m] = endTime.split(':').map(Number);
        const end = new Date();
        end.setHours(h, m, 0, 0);
        if (end.getTime() <= Date.now()) end.setDate(end.getDate() + 1);
        onDone(end.getTime(), totalMinutes, knockDoor, textMessage);
    };

    const isDoneDisabled = totalMinutes <= 0 || timePastError;

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            maxWidth="600px"
            dialogContentProps={{
                type: DialogType.close,
                title: IvicosStrings.focusTimeModalTitle,
                closeButtonAriaLabel: IvicosStrings.focusTimeCloseAriaLabel
            }}
            modalProps={{
                isBlocking: true
            }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 568, overflow: 'hidden' } }}>
                {/* Select duration */}
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    {IvicosStrings.focusTimeSelectDuration}
                </Text>
                <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
                    {DURATION_PRESETS.map((min) => (
                        <button
                            key={min}
                            type="button"
                            onClick={() => handleDurationPreset(min)}
                            style={{
                                padding: '8px 12px',
                                borderRadius: 4,
                                border: '1px solid ' + (selectedPreset === min ? theme.palette.themePrimary : theme.palette.neutralLight),
                                backgroundColor: selectedPreset === min ? theme.palette.themePrimary : theme.palette.neutralLighter,
                                color: selectedPreset === min ? theme.palette.white : theme.palette.black,
                                cursor: 'pointer',
                                fontSize: 13
                            }}
                        >
                            {getDurationLabel(min)}
                        </button>
                    ))}
                </Stack>
                {/* Dropdown for extended durations (15 min – 4 h) */}
                <Dropdown
                    label={IvicosStrings.focusTimeMore}
                    placeholder={IvicosStrings.focusTimeMore}
                    options={durationDropdownOptions}
                    selectedKey={selectedPreset != null && EXTENDED_PRESET_VALUES.has(selectedPreset) ? selectedPreset : undefined}
                    onChange={(_, opt) => opt && handleDurationPreset(Number(opt.key))}
                    styles={{ root: { maxWidth: 200 }, dropdown: { borderRadius: 4 } }}
                />
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                    <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <TextField
                            type="number"
                            value={String(Math.floor(totalMinutes / 60))}
                            onChange={handleCustomHoursChange}
                            styles={{ root: { width: 56 }, fieldGroup: { borderRadius: 4 } }}
                        />
                        <Text variant="small">h</Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <TextField
                            type="number"
                            value={String(totalMinutes % 60)}
                            onChange={handleCustomMinutesChange}
                            styles={{ root: { width: 56 }, fieldGroup: { borderRadius: 4 } }}
                        />
                        <Text variant="small">min</Text>
                    </Stack>
                </Stack>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                        {IvicosStrings.focusTimeTotalDuration}: {totalMinutes} min
                    </Text>
                    {maxDurationError && (
                        <Text
                            variant="small"
                            style={{
                                color: theme.palette.red,
                                maxWidth: '100%',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word'
                            }}
                        >
                            {IvicosStrings.focusTimeMaxDurationError}
                        </Text>
                    )}
                </Stack>

                {/* Set time interval */}
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    {IvicosStrings.focusTimeSetTimeInterval}
                </Text>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Stack horizontal tokens={{ childrenGap: 16 }}>
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text variant="small">{IvicosStrings.focusTimeStartTime}</Text>
                            <TextField
                                value={startTime}
                                onChange={handleStartTimeChange}
                                placeholder="09:00"
                                styles={{ root: { width: 100 }, fieldGroup: { borderRadius: 4 } }}
                            />
                        </Stack>
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text variant="small">{IvicosStrings.focusTimeEndTime}</Text>
                            <TextField
                                value={endTime}
                                onChange={handleEndTimeChange}
                                placeholder="09:15"
                                styles={{ root: { width: 100 }, fieldGroup: { borderRadius: 4 } }}
                            />
                        </Stack>
                    </Stack>
                    {timePastError && (
                        <Text
                            variant="small"
                            style={{
                                color: theme.palette.red,
                                maxWidth: '100%',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word'
                            }}
                        >
                            {IvicosStrings.focusTimeStartOrEndTimePastError}
                        </Text>
                    )}
                </Stack>

                {/* Emergency contact */}
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                    {IvicosStrings.focusTimeEmergencyQuestion}
                </Text>
                <Stack tokens={{ childrenGap: 8 }}>
                    <Checkbox label={IvicosStrings.focusTimeKnockDoor} checked={knockDoor} onChange={(_, c) => setKnockDoor(!!c)} />
                    <Checkbox label={IvicosStrings.focusTimeTextMessage} checked={textMessage} onChange={(_, c) => setTextMessage(!!c)} />
                </Stack>

                {/* Actions */}
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        onClick={onDismiss}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 4,
                            border: '1px solid ' + theme.palette.neutralLight,
                            backgroundColor: 'transparent',
                            color: theme.palette.black,
                            cursor: 'pointer',
                            fontSize: 14
                        }}
                    >
                        {IvicosStrings.focusTimeCancel}
                    </button>
                    <PrimaryButton text={IvicosStrings.focusTimeDone} onClick={handleDone} disabled={isDoneDisabled} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default FocusTimeModal;
